import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import PremiumCollection from "./PremiumCollection";

const LightEnhancing = () => {
  const roomRef = useRef(null);
  const lightBeamRef = useRef(null);

  useEffect(() => {
    // Light beam animation
    if (lightBeamRef.current) {
      gsap.fromTo(
        lightBeamRef.current,
        { opacity: 0.3, y: -50 },
        {
          opacity: 1,
          y: 0,
          duration: 2,
          ease: "power2.inOut",
          repeat: -1,
          yoyo: true,
        }
      );
    }

    // Room brightness animation
    if (roomRef.current) {
      gsap.fromTo(
        roomRef.current,
        { backgroundColor: "rgba(220, 220, 220, 1)" },
        {
          backgroundColor: "rgba(255, 250, 230, 1)",
          duration: 3,
          yoyo: true,
          repeat: -1,
        }
      );
    }
  }, []);

  return (
    <div className="bg-gray-50 font-playfair">
      {/* Landing Section */}
      <section className="relative h-screen bg-black text-white flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="images/landing.png"
            alt="Landing"
            className="w-full h-full object-cover opacity-70"
          />
        </div>
        <div className="relative z-10 text-center">
          <h1 className="text-5xl lg:text-7xl xl:text-9xl font-bold mb-4">Transform Your Space</h1>
          <p className="text-lg max-w-3xl lg:text-2xl mx-auto">
            Explore the world of light and color. Transform your home into a
            serene retreat with our light-enhancing solutions that bring nature’s
            beauty indoors.
          </p>
        </div>
      </section>

      {/* Vertical Stacked Content */}
      <section className="py-16 bg-white px-3">
        <div className="container mx-auto space-y-12">
          {/* Section 1 */}
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="lg:w-full">
              <img
                src="images/landing4.png"
                alt="Shade 1"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
            <div className="lg:w-1/2 text-center lg:text-left">
              <h3 className="text-3xl font-bold mb-4">
                Elegance Meets Functionality
              </h3>
              <p className="text-gray-600">
                Our light-enhancing shades offer more than just aesthetic appeal.
                With their sleek design, they add sophistication to any room
                while providing excellent light control. Whether it’s your cozy
                bedroom or a lively living area, these shades complement your
                space effortlessly.
              </p>
              <p className="text-gray-600 mt-4">
                Embrace the beauty of simplicity and redefine your interior with
                shades that make a statement.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="flex flex-col lg:flex-row-reverse items-center gap-8">
            <div className="lg:w-full">
              <img
                src="images/landing1.png"
                alt="Shade 2"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
            <div className="lg:w-1/2 text-center lg:text-left">
              <h3 className="text-3xl font-bold mb-4">
                Control Light, Control Life
              </h3>
              <p className="text-gray-600">
                Experience the perfect balance between natural and artificial
                lighting with shades that allow you to fine-tune brightness and
                temperature. Create a calming atmosphere for relaxing evenings
                or brighten up the room for productive days.
              </p>
              <p className="text-gray-600 mt-4">
                With our advanced design, you can enjoy ultimate flexibility to
                match your daily rhythms.
              </p>
            </div>
          </div>

          {/* Section 3 */}
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="lg:w-full">
              <img
                src="images/landing.png"
                alt="Shade 3"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
            <div className="lg:w-1/2 text-center lg:text-left">
              <h3 className="text-3xl font-bold mb-4">
                Shades for Every Space
              </h3>
              <p className="text-gray-600">
                No matter the size or style of your room, our shades are tailored
                to fit seamlessly into every space. From modern apartments to
                traditional homes, their versatility ensures that your
                environment feels complete and harmonious.
              </p>
              <p className="text-gray-600 mt-4">
                Their durable material and timeless design make them a long-term
                investment in style and functionality.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100 ">
  <div className="container mx-auto text-center">
    <h2 className="text-4xl font-bold mb-8">Meet Our Experts Team</h2>
    <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
      Our team of skilled professionals brings years of experience and
      innovation to every project, ensuring the highest level of craftsmanship
      and expertise.
    </p>

    <div className="bg-white p-2 rounded-lg shadow-xl max-w-7xl  mx-auto">
      {/* Team Members Grid with 2 images per row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
        {/* Row 1 */}
        <div className="flex flex-col items-center bg-gray-200 p-2 rounded-lg shadow-md">
          <div className=" mb-4 relative">
            <img
              src="images/landing4.png"
              alt="Expert 1"
              className="w-[400px] object-cover rounded-full"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black opacity-50 rounded-full"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800">John Doe</h3>
          <p className="text-gray-500">Lead Designer</p>
        </div>

        <div className="flex flex-col items-center bg-gray-200 p-2 rounded-lg shadow-md">
          <div className=" mb-4 relative">
            <img
              src="images/landing2.png"
              alt="Expert 2"
              className="w-[400px] object-cover rounded-full"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black opacity-50 rounded-full"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Jane Smith</h3>
          <p className="text-gray-500">Light Specialist</p>
        </div>

        {/* Row 2 */}
        <div className="flex flex-col items-center bg-gray-200 p-2 rounded-lg shadow-md">
          <div className=" mb-4 relative">
            <img
              src="images/landing2.png"
              alt="Expert 3"
              className="w-[400px] object-cover rounded-full"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black opacity-50 rounded-full"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Robert Brown</h3>
          <p className="text-gray-500">Interior Architect</p>
        </div>

        <div className="flex flex-col items-center bg-gray-200 p-5 rounded-lg shadow-md">
          <div className="mb-4 relative">
            <img
              src="images/landing.png"
              alt="Expert 4"
              className="w-[400px] object-cover rounded-full"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black opacity-50 rounded-full"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Emily Johnson</h3>
          <p className="text-gray-500">Project Manager</p>
        </div>
      </div>
    </div>
  </div>
</section>

<PremiumCollection/>
<section className="relative bg-cover bg-center py-24 " style={{  backgroundImage: "url('images/landing.png') " }}>
  <div className="absolute inset-0 bg-black bg-opacity-50"></div>
  <div className="relative z-10 container mx-auto text-center text-white px-6">
    <h2 className="text-4xl md:text-5xl font-bold mb-6">Elevate Your Space with Custom Blinds</h2>
    <p className="text-lg md:text-xl mb-8">
      Whether you're looking for elegance, functionality, or a touch of luxury,<br/> we've got you covered. 
      Get in touch today to find the perfect blinds for your home or office!
    </p>
    <a
      href="/contact"
      className="inline-block bg-[#ac9b82] text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:bg-[#9e7f52] transition duration-300"
    >
      Request Your Free Quote
    </a>
  </div>
</section>
    </div>
  );
};

export default LightEnhancing;







// import React, { useEffect, useRef } from "react";
// import { gsap } from "gsap";

// const LightEnhancing = () => {
//   const roomRef = useRef(null);
//   const lightBeamRef = useRef(null);

//   useEffect(() => {
//     // Animate the light beam on component load
//     gsap.fromTo(
//       lightBeamRef.current,
//       { opacity: 0, y: -50 },
//       { opacity: 1, y: 0, duration: 2, ease: "power2.out", repeat: -1, yoyo: true }
//     );

//     // Animate the room brightness
//     gsap.fromTo(
//       roomRef.current,
//       { backgroundColor: "rgba(220, 220, 220, 1)" },
//       { backgroundColor: "rgba(255, 250, 230, 1)", duration: 3, yoyo: true, repeat: -1 }
//     );
//   }, []);

//   return (
//     <div className="relative h-screen bg-black  flex items-center justify-center" >
//       {/* Room Background */}
//       <div
//         ref={roomRef}
//         className="relative w-full h-full object-cover bg-gray-200 rounded-lg shadow-lg overflow-hidden"
//         style={{  backgroundImage: "url('images/landbg4.jpeg') " }}
//       >
//         {/* Light Beam */}
//         <div
//           ref={lightBeamRef}
//           className="absolute top-0  w-full h-full bg-gradient-to-b from-yellow-100 via-transparent to-transparent opacity-50 pointer-events-none"
//         ></div>

//         {/* Content */}
//         <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
//           <h1 className="text-4xl font-bold text-gray-800 mb-4">
//             Illuminate Your Space
//           </h1>
//           <p className="text-lg text-gray-600 max-w-md text-center">
//             Experience the beauty of natural light with our innovative blinds.
//             Enhance your room’s ambiance, reduce energy usage, and let the
//             sunshine transform your home.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default LightEnhancing;
